import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { Plus, Trash2, Users, Sparkles, GraduationCap, Copy } from "lucide-react";
import { toast } from "sonner";

const EMOJIS = ["🦸", "🦸‍♀️", "🧙", "🧚", "🦄", "🐉", "🚀", "🐱", "🐶", "🦊"];

export default function TeacherDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [classrooms, setClassrooms] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [students, setStudents] = useState([]);
  const [comics, setComics] = useState([]);
  const [openCls, setOpenCls] = useState(false);
  const [openStu, setOpenStu] = useState(false);
  const [newClsName, setNewClsName] = useState("");
  const [stu, setStu] = useState({ name: "", age: 8, reading_level: "early", avatar_emoji: "🦸" });

  const loadAll = async () => {
    const r = await api.get("/classrooms");
    setClassrooms(r.data);
    if (r.data.length && !activeId) setActiveId(r.data[0].id);
  };

  useEffect(() => { if (user?.role === "teacher") loadAll(); }, [user?.role]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!activeId) return;
    Promise.all([
      api.get(`/classrooms/${activeId}/students`),
      api.get(`/classrooms/${activeId}/comics`),
    ]).then(([s, c]) => { setStudents(s.data); setComics(c.data); });
  }, [activeId]);

  if (user && user.role !== "teacher") {
    return (
      <div className="min-h-screen bg-zinc-50">
        <Navbar/>
        <div className="max-w-md mx-auto p-12">
          <div className="cv-border cv-shadow-lg rounded-2xl bg-yellow-100 p-8 text-center">
            <h1 className="font-headings uppercase text-3xl mb-3">Teacher account needed</h1>
            <p className="font-body font-bold mb-6">This area is for educators only. Sign up with a teacher account to access classroom features.</p>
            <Link to="/dashboard"><Button className="bg-purple-600 hover:bg-purple-500 text-white cv-border cv-shadow cv-press font-headings uppercase rounded-xl">Back to dashboard</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  const createCls = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/classrooms", { name: newClsName });
      toast.success(`Classroom created! Code: ${r.data.join_code}`);
      setOpenCls(false); setNewClsName("");
      loadAll(); setActiveId(r.data.id);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const addStudent = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/classrooms/${activeId}/students`, { ...stu, age: parseInt(stu.age) });
      toast.success(`${stu.name} added`);
      setOpenStu(false);
      setStu({ name: "", age: 8, reading_level: "early", avatar_emoji: "🦸" });
      const s = await api.get(`/classrooms/${activeId}/students`);
      setStudents(s.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const deleteCls = async (id) => {
    if (!window.confirm("Delete this classroom and all its students?")) return;
    await api.delete(`/classrooms/${id}`);
    setActiveId(null);
    loadAll();
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied!");
  };

  const active = classrooms.find(c => c.id === activeId);

  return (
    <div className="min-h-screen bg-zinc-50">
      <Navbar/>
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <div className="inline-flex items-center gap-2 bg-purple-500 text-white cv-border cv-shadow-sm rounded-xl px-3 py-1 mb-2"><GraduationCap size={18}/><span className="font-headings uppercase">Teacher</span></div>
            <h1 className="font-headings uppercase text-4xl md:text-5xl" data-testid="teacher-title">Welcome, {user?.name}</h1>
            <p className="font-body font-bold text-black/70 mt-1">Manage your classrooms (up to 30 students each).</p>
          </div>
          <Dialog open={openCls} onOpenChange={setOpenCls}>
            <DialogTrigger asChild>
              <Button className="bg-yellow-400 hover:bg-yellow-300 text-black cv-border cv-shadow cv-press font-headings uppercase rounded-xl" data-testid="add-classroom-btn">
                <Plus size={18} className="mr-1"/> New classroom
              </Button>
            </DialogTrigger>
            <DialogContent className="cv-border cv-shadow-lg rounded-2xl bg-white">
              <DialogHeader><DialogTitle className="font-headings uppercase text-2xl">New classroom</DialogTitle></DialogHeader>
              <form onSubmit={createCls} className="space-y-3">
                <div>
                  <Label className="font-headings uppercase">Name</Label>
                  <Input required className="cv-border-3 mt-1 rounded-xl font-body font-bold" value={newClsName} onChange={(e)=>setNewClsName(e.target.value)} placeholder="Mrs. Park's 3rd Grade" data-testid="classroom-name"/>
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-purple-600 hover:bg-purple-500 text-white cv-border cv-shadow-sm cv-press font-headings uppercase rounded-xl" data-testid="classroom-submit">Create</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {classrooms.length === 0 ? (
          <div className="cv-border cv-shadow rounded-2xl bg-yellow-100 p-12 text-center font-body font-bold">
            No classrooms yet — tap "New classroom" to get started! 🎓
          </div>
        ) : (
          <>
            <div className="flex gap-3 flex-wrap mb-6">
              {classrooms.map(c => (
                <button key={c.id} onClick={()=>setActiveId(c.id)}
                        className={`cv-border-3 px-4 py-2 rounded-xl font-headings uppercase cv-press ${activeId === c.id ? "bg-yellow-300 cv-shadow-sm" : "bg-white"}`}
                        data-testid={`classroom-tab-${c.id}`}>
                  {c.name} <span className="font-body text-xs ml-1">({c.student_count})</span>
                </button>
              ))}
            </div>

            {active && (
              <>
                <div className="cv-border cv-shadow rounded-2xl bg-white p-6 mb-6 flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h2 className="font-headings uppercase text-2xl">{active.name}</h2>
                    <p className="font-body font-bold text-sm text-black/70">{active.student_count}/30 students · {active.comic_count} comics</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-body font-bold text-sm">Join code:</span>
                    <code className="cv-border-3 bg-yellow-300 px-3 py-1 rounded-lg font-headings text-xl" data-testid="classroom-code">{active.join_code}</code>
                    <button onClick={()=>copyCode(active.join_code)} className="cv-border-3 bg-white p-2 rounded-lg cv-shadow-sm cv-press"><Copy size={16}/></button>
                    <button onClick={()=>deleteCls(active.id)} className="cv-border-3 bg-red-100 p-2 rounded-lg cv-shadow-sm cv-press" data-testid="delete-classroom"><Trash2 size={16}/></button>
                  </div>
                </div>

                <section className="mb-10">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-headings uppercase text-xl flex items-center gap-2"><Users size={22}/> Students</h3>
                    <Dialog open={openStu} onOpenChange={setOpenStu}>
                      <DialogTrigger asChild>
                        <Button disabled={active.student_count >= 30} className="bg-pink-500 hover:bg-pink-400 text-white cv-border cv-shadow-sm cv-press font-headings uppercase rounded-xl" data-testid="add-student-btn"><Plus size={16}/> Student</Button>
                      </DialogTrigger>
                      <DialogContent className="cv-border cv-shadow-lg rounded-2xl bg-white">
                        <DialogHeader><DialogTitle className="font-headings uppercase text-2xl">Add student</DialogTitle></DialogHeader>
                        <form onSubmit={addStudent} className="space-y-3">
                          <div>
                            <Label className="font-headings uppercase">Name</Label>
                            <Input required className="cv-border-3 mt-1 rounded-xl font-body font-bold" value={stu.name} onChange={(e)=>setStu({...stu, name: e.target.value})} data-testid="student-name"/>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="font-headings uppercase">Age</Label>
                              <Input type="number" min={5} max={14} required className="cv-border-3 mt-1 rounded-xl font-body font-bold" value={stu.age} onChange={(e)=>setStu({...stu, age: e.target.value})} data-testid="student-age"/>
                            </div>
                            <div>
                              <Label className="font-headings uppercase">Reading</Label>
                              <Select value={stu.reading_level} onValueChange={(v)=>setStu({...stu, reading_level: v})}>
                                <SelectTrigger className="cv-border-3 mt-1 rounded-xl font-body font-bold"><SelectValue/></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="early">Early reader</SelectItem>
                                  <SelectItem value="fluent">Fluent reader</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <Label className="font-headings uppercase mb-2 block">Avatar</Label>
                            <div className="flex flex-wrap gap-2">
                              {EMOJIS.map(e => (
                                <button type="button" key={e} onClick={()=>setStu({...stu, avatar_emoji: e})}
                                  className={`w-12 h-12 cv-border-3 rounded-xl text-2xl flex items-center justify-center ${stu.avatar_emoji === e ? "bg-yellow-300 cv-shadow-sm" : "bg-white"}`}>
                                  {e}
                                </button>
                              ))}
                            </div>
                          </div>
                          <DialogFooter>
                            <Button type="submit" className="bg-purple-600 hover:bg-purple-500 text-white cv-border cv-shadow-sm cv-press font-headings uppercase rounded-xl" data-testid="student-submit">Add student</Button>
                          </DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                    {students.map(s => (
                      <button key={s.id} onClick={()=>navigate(`/create?child=${s.id}`)} className="cv-border-3 cv-shadow-sm rounded-xl bg-white p-3 cv-press text-left" data-testid={`student-${s.id}`}>
                        <div className="text-3xl mb-1">{s.avatar_emoji}</div>
                        <div className="font-headings uppercase truncate">{s.name}</div>
                        <div className="font-body text-xs text-black/60">Age {s.age}</div>
                      </button>
                    ))}
                    {students.length === 0 && (
                      <div className="col-span-full font-body font-bold text-black/60 italic">No students yet — add some!</div>
                    )}
                  </div>
                </section>

                <section>
                  <h3 className="font-headings uppercase text-xl flex items-center gap-2 mb-4"><Sparkles size={22}/> Class comics</h3>
                  <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {comics.map(c => (
                      <Link to={`/comic/${c.id}`} key={c.id} className="cv-border cv-shadow-sm rounded-xl bg-white overflow-hidden cv-press">
                        <div className="aspect-square halftone-purple flex items-center justify-center">
                          {c.panels?.[0]?.image_url ? <img src={c.panels[0].image_url} alt="" className="w-full h-full object-cover"/> : <Sparkles size={32}/>}
                        </div>
                        <div className="p-2">
                          <div className="font-headings uppercase truncate text-sm">{c.title}</div>
                          <div className="font-body text-xs text-black/60">{c.status}</div>
                        </div>
                      </Link>
                    ))}
                    {comics.length === 0 && (
                      <div className="col-span-full font-body font-bold text-black/60 italic">No comics yet from this class.</div>
                    )}
                  </div>
                </section>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
